import { OverlayRef } from '@angular/cdk/overlay';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BehaviorSubject, filter, Subject, takeUntil } from 'rxjs';
import { ToastsService } from '../../services/toast.service';
import { ToastOutlet, ToastType } from '../../types/enums';
import { Toast } from '../../types/toast.types';

@Component({
  selector: 'ag2-weathermaker-toasts',
  templateUrl: './toast.component.html',
  styleUrls: ['./toast.component.scss'],
  imports: [NgClass, NgTemplateOutlet],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToastsComponent implements OnInit, OnDestroy {
  private readonly _toasts = new BehaviorSubject<Toast[]>([]);
  toastsSignal = toSignal(this._toasts.asObservable(), { initialValue: [] });

  public set toasts(value: Toast[]) {
    this._toasts.next(value);
  }
  public get toasts(): Toast[] {
    return this._toasts.getValue();
  }

  public overlayRef!: OverlayRef;
  public toastOutlet: ToastOutlet = ToastOutlet.Global;
  public playDismissAnimation = false;
  public showStack = false;

  private readonly destroy$ = new Subject<void>();
  private dismissTimer?: ReturnType<typeof setTimeout>;

  private readonly TOAST_DURATIONS: Record<ToastType, number> = {
    [ToastType.Error]: 15000,
    [ToastType.Success]: 5000,
  };

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly toastsService = inject(ToastsService);

  ngOnInit(): void {
    this._toasts
      .asObservable()
      .pipe(
        takeUntil(this.destroy$),
        filter((toasts) => toasts.length > 0)
      )
      .subscribe((toasts) => {
        this.handleToastsChange(toasts);
      });
  }

  private handleToastsChange(toasts: Toast[]): void {
    if (toasts.length > 1) {
      this.showStack = true;
      this.cdr.markForCheck();
    } else if (toasts.length === 1) {
      setTimeout(() => {
        this.showStack = false;
        this.cdr.markForCheck();
      }, 250);
    }

    if (!this.dismissTimer && toasts.length > 0) {
      const duration = this.TOAST_DURATIONS[toasts[0].type];
      this.dismissTimer = setTimeout(() => {
        this.dismiss();
      }, duration);
    }
  }

  ngOnDestroy(): void {
    if (this.dismissTimer) {
      clearTimeout(this.dismissTimer);
    }

    this.destroy$.next();
    this.destroy$.complete();
    this._toasts.complete();
  }

  public dismiss(): void {
    if (this.dismissTimer) {
      clearTimeout(this.dismissTimer);
      this.dismissTimer = undefined;
    }

    if (this.toasts.length === 0) {
      this.toastsService.disposeIfNoToasts(this.toastOutlet);
      return;
    }

    this.playDismissAnimation = true;
    setTimeout(() => {
      this.playDismissAnimation = false;
      this.cdr.markForCheck();

      if (this.toasts.length === 0) {
        this.toastsService.disposeIfNoToasts(this.toastOutlet);
      }
    }, 500);

    this.toasts = this.toasts.slice(1);
  }
}
